import axiosInstance from 'config/axiosInstance';
import { PAGE_SIZE } from 'config/constant';
import React, { useCallback, useEffect, useState } from 'react';
import DateRangeFilter from 'reusable/datefilterrange';
import Export from 'reusable/export';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import { useTranslation } from 'react-i18next';

const sortingOptions = [
  { label: 'Default', value: '' },
  { label: 'Submit Time', value: 'testing_time' },
];

interface DataItem {
  id: number;
  name: string;
}

let data: DataItem[] = [];
data.push({ id: 1, name: 'Item 1' });

interface DriverLicenseProps {
  id?: string;
}

const DriverLicenseSubmissionsTable: React.FC<DriverLicenseProps> = ({
  id,
}) => {
  const { t, i18n } = useTranslation('driver');
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortValue, setSortValue] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [driverLicense, setDriverLicense] = useState<any[]>([]);

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const formattedDate = i18n.language === 'jp'
      ? `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`
      : `${date.getDate().toString().padStart(2, '0')} ${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
    const time = timeFormatter.format(date);
    return { time, formattedDate };
  };

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  };

  const exportFields = [
    { header: 'Driver Name', field: 'user.name' },
    { header: 'Driver Phone', field: 'user.phone' },
    { header: 'Test Result', field: 'data' },
    { header: 'Date & Time', field: 'testing_time' },
  ];

  const fetchData = useCallback(async () => {
    try {
      let apiUrl = '/v1/scan_ic_records/?';
      if (dateRange.startDate && dateRange.endDate) {
        apiUrl += `check_in__gte=${dateRange.startDate}T00%3A00%3A00.000000Z&check_in__lte=${dateRange.endDate}T23%3A59%3A59.999999Z&`;
      }
      apiUrl += `page=${currentPage}&page_size=${PAGE_SIZE}&user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      setCount(response.data.count);
      setDriverLicense(response.data.results);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  }, [currentPage, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        {/* ToolBar Section */}
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <div className="flex flex-row space-x-2">
          <DateRangeFilter onDateRangeChange={handleDateRangeChange} />
          <Export data={data} exportFields={exportFields} />
        </div>
      </div>
      {/* Table */}
      <div>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1 text-center">{t('view.license-submission.submit-date')}</h6>
          <h6 className="flex-1 text-center">{t('view.license-submission.submit-time')}</h6>
          <h6 className="flex-1 text-center">{t('view.license-submission.location')}</h6>
        </div>
        <div>
          {driverLicense.map((license, index) => {
            const { time, formattedDate } = formatDateTime(license.create_time);
            return (
              <div key={index} className="flex flex-row py-3 px-4">
                <p className="flex-1 text-center">{formattedDate}</p>
                <p className="flex-1 text-center">{time}</p>
                <p className="flex-1 text-center">{license.location_name}</p>
              </div>
            );
          })}
        </div>
      </div>
      {/* Pagination Section */}
      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

export default DriverLicenseSubmissionsTable;